// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { useDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import useZeroSelector from '~/hooks/useZeroSelector';
import { objectIsEmpty, renameParamKey, setGridLoading } from '~/other/Helper';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useScheduleDataGridContext } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.context';
import { ScheduleDataGridModals } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.modals';
import { EventNames, getColumnDefs, getSortByColumnName, loadSchedules } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.utils';

const SCHEDULES_PER_PAGE = 50;

function useScheduleDataGridQuery() {
    const formsQuery = useZeroSelector(state => state.forms.query);
    const [query, setQuery] = useState({ formsQuery });

    useEffect(() => {
        setQuery({ formsQuery });
    }, [formsQuery]);

    return query;
}

/**
 * @param {{
 *  pageType: string,
 *  showCreateModal: boolean,
 *  onCloseCreateModal: () => void;
 * }} props
 */
export default function ScheduleDataGrid({ pageType, showCreateModal, onCloseCreateModal }) {
    const location = useLocation();
    const { gridRef, settings, setSettings, rowCount } = useScheduleDataGridContext();
    const query = useScheduleDataGridQuery();

    const colDefs = useMemo(() => getColumnDefs(pageType), [pageType]);

    const getRows = useCallback(async (/** @type {AgGrid.IGetRowsParams} */params) => {
        const perPage = SCHEDULES_PER_PAGE;
        const currentPage = Math.floor((params.startRow ?? 0) / perPage) + 1;

        const { formsQuery } = query;

        const queryParams = new URLSearchParams(formsQuery);
        queryParams.set("form_types", pageType);
        queryParams.set("page", String(currentPage));
        queryParams.set("per_page", String(perPage));
        
        // Apply transformations that were in original SchedulesTable
        renameParamKey(queryParams, "schedule_status", "status");
        renameParamKey(queryParams, "category_uuid", "category_uuids");

        const teamIdParam = queryParams.get("team_uuid");
        if (teamIdParam === "my_teams" || teamIdParam === "organization_teams") {
            queryParams.delete("team_uuid");
        }

        if (!objectIsEmpty(params.filterModel)) {
            queryParams.set('ag_filter', JSON.stringify(params.filterModel));
        }

        params.sortModel.forEach(sortModel => {
            let col = getSortByColumnName(sortModel.colId);
            if (sortModel.sort === 'desc') {
                col = `-${col}`;
            }
            queryParams.append('sort_by', col);
        });

        const gridApi = gridRef.current?.api;
        if (rowCount === null) {
            setGridLoading(gridApi, true);
        }
        
        const { schedules, totalSchedules } = await loadSchedules(queryParams);

        params.successCallback(schedules, totalSchedules);
        if (rowCount === null) {
            setGridLoading(gridApi, false);
        }
    }, [rowCount, query, pageType]);

    const clearScheduleCache = useCallback(() => {
        loadSchedules.cache.clear();
    }, []);

    const { onGridReady, onStateUpdated } = useDataGridSetup({
        eventNames: EventNames,
        autoLoadData: true,
        gridRef,
        colDefs,
        getRows,
        setSettings,
        onReset: clearScheduleCache,
        onRefresh: clearScheduleCache,
    });
    
    useEffect(() => {
        // Clear memoization when component is unmounted
        return () => {
            clearScheduleCache();
        }
    }, [clearScheduleCache]);

    return (
        <>
            <ZeroDataGrid
                gridRef={gridRef}
                columnDefs={colDefs}
                defaultColDef={{
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
                initialState={location.state?.gridState ?? settings}
                onGridReady={onGridReady}
                onStateUpdated={onStateUpdated}
                cacheBlockSize={SCHEDULES_PER_PAGE}
                cacheOverflowSize={2}
                maxConcurrentDatasourceRequests={2}
                infiniteInitialRowCount={rowCount ?? SCHEDULES_PER_PAGE}
                pagination={true}
                paginationPageSizeSelector={false}
                paginationPageSize={rowCount ?? SCHEDULES_PER_PAGE}
                tooltipShowDelay={500}
                tooltipShowMode="standard"
                suppressCellFocus
            />
            <ScheduleDataGridModals 
                showCreateModal={showCreateModal} 
                onCloseCreateModal={onCloseCreateModal}
                pageType={pageType}
            />
        </>
    );
}
